import { EditIcon, Group, InfiniteTable } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';

import {
  AccountReceivable,
  AccountReceivableOrderColumn,
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { DirectSale } from '@/core/domain/sales/DirectSale/DirectSale.types';
import { useStoneDeviceList } from '@/core/domain/stone/devices/devices.hooks';
import { quantityFormatter } from '@/utils/number';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Popover, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import moment from 'moment';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

type Props = {
  installments: AccountReceivable[];
  isFetching: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  directSale?: DirectSale;
};

const DirectSaleListInstallments: React.FC<Props> = ({
  installments,
  isFetching,
  hasNextPage,
  fetchNextPage,
  directSale,
}) => {
  const intl = useIntl();

  const stoneDevices = useStoneDeviceList({
    companyId: directSale?.companyId,
    isActive: true,
  });

  const getDevice = useCallback(
    (deviceId: number) =>
      stoneDevices.data?.find((device) => device.id === deviceId),
    [stoneDevices.dataUpdatedAt],
  );

  const columns: ColumnsType<AccountReceivable> = [
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.code',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.code,
    },
    {
      width: 220,
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.companies',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.company,
    },
    {
      width: 220,
      dataIndex: 'debtor',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.debtor',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.debtor,
    },
    {
      width: COLUMNS_WIDTH.action2,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.description',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.description,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.documentType',
      }),
    },
    {
      width: 100,
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.dueDate,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.situation',
      }),
      render: (situation: AccountReceivableSituation) => (
        <StatusTag
          color={AccountReceivableSituationColor[situation]}
          label={AccountRecivableSituationType[situation]}
        />
      ),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'installmentValue',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.value',
      }),
      sorter: true,
      align: 'right',
      key: AccountReceivableOrderColumn.value,
      render: (_, item) =>
        `R$ ${quantityFormatter(
          item.installmentValue +
            (item.discountIncrease ?? 0) -
            (item.rateValue ?? 0),
        )}`,
    },
    {
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.actions',
      }),
      width: 60,
      align: 'right',
      fixed: 'right',
      render: (_, item) => (
        <Group position="right" gap={12}>
          {!!item?.isStoneOrder && !!item?.stoneDeviceId && (
            <Popover
              content={`Dispositivo: ${
                getDevice(item?.stoneDeviceId)?.serialNumber
              }`}
            >
              <img
                src="/images/device-stone.png"
                alt="Device Stone"
                width={16}
              />
            </Popover>
          )}

          <EditIcon
            iconProps={{
              onClick: () =>
                window.open(
                  `/app/financial/billToReceiver/releaseBillToReceiver/${item.accountReceivableId}/installment/${item.id}`,
                  '_blank',
                ),
            }}
          />
        </Group>
      ),
    },
  ];

  return (
    <>
      <InfiniteTable<AccountReceivable>
        columns={columns}
        dataSource={installments}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        title={() => (
          <Typography.Title level={4}>Parcelas Geradas</Typography.Title>
        )}
      />
    </>
  );
};

export default DirectSaleListInstallments;
